const toSortedIds = (items) => [...new Set(items.filter(Boolean))].sort();

const stateNodeId = (stateId) => `state:${stateId}`;
const activityPreNodeId = (actionId) => `activity_pre:${actionId}`;
const activityPostNodeId = (actionId) => `activity_post:${actionId}`;

const addToMapList = (map, key, value) => {
  if (!key) return;
  if (!map[key]) {
    map[key] = [];
  }
  map[key].push(value);
};

const ensureNode = (graph, node) => {
  if (!graph.nodes[node.id]) {
    graph.nodes[node.id] = node;
  }
  if (!graph.outgoing[node.id]) {
    graph.outgoing[node.id] = [];
  }
  if (!graph.incoming[node.id]) {
    graph.incoming[node.id] = [];
  }
  addToMapList(graph.byRole, node.roleId, node.id);
  addToMapList(graph.byThread, node.threadId, node.id);
};

const addEdge = (graph, edge) => {
  if (graph.edges[edge.id]) return;
  graph.edges[edge.id] = edge;
  if (!graph.outgoing[edge.from]) {
    graph.outgoing[edge.from] = [];
  }
  if (!graph.incoming[edge.to]) {
    graph.incoming[edge.to] = [];
  }
  graph.outgoing[edge.from].push(edge.id);
  graph.incoming[edge.to].push(edge.id);
};

const resolveThreadEntryStateId = (threadId, context) => {
  const actions = Object.values(context.actionsById).filter(
    (action) => action.threadId === threadId,
  );
  if (!actions.length) return null;
  const pres = actions.map((action) => action.pre);
  const posts = new Set(actions.map((action) => action.post));
  const candidates = toSortedIds(pres.filter((id) => !posts.has(id)));
  if (candidates.length) return candidates[0];
  return toSortedIds(pres)[0] ?? null;
};

const resolveThreadExitStateId = (threadId, context) => {
  const actions = Object.values(context.actionsById).filter(
    (action) => action.threadId === threadId,
  );
  if (!actions.length) return null;
  const posts = actions.map((action) => action.post);
  const pres = new Set(actions.map((action) => action.pre));
  const candidates = toSortedIds(posts.filter((id) => !pres.has(id)));
  if (candidates.length) return candidates[candidates.length - 1];
  return toSortedIds(posts).at(-1) ?? null;
};

export const buildReachabilityContext = (rad) => {
  // Context is an indexed view of the RAD used by graph construction and traversal.
  // The runtime assumes normalized vnext-style fields are available (threadId/roleId/pre/post).
  const statesById = Object.fromEntries((rad.states ?? []).map((s) => [s.id, s]));
  const threadsById = Object.fromEntries((rad.threads ?? []).map((t) => [t.id, t]));
  const actionsById = Object.fromEntries(
    (rad.activities ?? []).map((a) => [a.id, a]),
  );
  const connectionsById = Object.fromEntries(
    (rad.connections ?? []).map((c) => [c.id, c]),
  );
  return {
    statesById,
    threadsById,
    actionsById,
    connectionsById,
  };
};

export const buildReachabilityGraph = (rad, context = buildReachabilityContext(rad)) => {
  // Graph model:
  // - State node: `state:<id>`
  // - Activity nodes: split into pre/post to make sequencing explicit
  //   state -> activity_pre -> activity_post -> state
  // This lets traversal reason about "crossing an interaction" precisely.
  const graph = {
    nodes: {},
    edges: {},
    outgoing: {},
    incoming: {},
    byRole: {},
    byThread: {},
  };

  toSortedIds((rad.states ?? []).map((state) => state.id)).forEach((stateId) => {
    const state = context.statesById[stateId];
    const thread = context.threadsById[state.threadId];
    ensureNode(graph, {
      id: stateNodeId(state.id),
      kind: "state",
      roleId: state.roleId ?? thread?.roleId,
      threadId: state.threadId,
      stateId: state.id,
    });
  });

  toSortedIds((rad.activities ?? []).map((action) => action.id)).forEach((actionId) => {
    const action = context.actionsById[actionId];
    const thread = context.threadsById[action.threadId];
    const roleId = action.roleId ?? thread?.roleId;

    ensureNode(graph, {
      id: activityPreNodeId(action.id),
      kind: "activity_pre",
      roleId,
      threadId: action.threadId,
      stateId: action.pre,
      actionId: action.id,
    });
    ensureNode(graph, {
      id: activityPostNodeId(action.id),
      kind: "activity_post",
      roleId,
      threadId: action.threadId,
      stateId: action.post,
      actionId: action.id,
    });

    // Local thread sequencing edges.
    addEdge(graph, {
      id: `seq:${stateNodeId(action.pre)}->${activityPreNodeId(action.id)}`,
      kind: "sequential",
      from: stateNodeId(action.pre),
      to: activityPreNodeId(action.id),
      roleId,
      threadId: action.threadId,
      actionId: action.id,
      temporalOrder: "forward",
    });
    addEdge(graph, {
      id: `seq:${activityPreNodeId(action.id)}->${activityPostNodeId(action.id)}`,
      kind: "sequential",
      from: activityPreNodeId(action.id),
      to: activityPostNodeId(action.id),
      roleId,
      threadId: action.threadId,
      actionId: action.id,
      temporalOrder: "forward",
    });
    addEdge(graph, {
      id: `seq:${activityPostNodeId(action.id)}->${stateNodeId(action.post)}`,
      kind: "sequential",
      from: activityPostNodeId(action.id),
      to: stateNodeId(action.post),
      roleId,
      threadId: action.threadId,
      actionId: action.id,
      temporalOrder: "forward",
    });
  });

  toSortedIds((rad.connections ?? []).map((connection) => connection.id)).forEach(
    (connectionId) => {
      const connection = context.connectionsById[connectionId];
      const interactionIds = toSortedIds(connection.interactionIds ?? []).filter((id) => {
        const action = context.actionsById[id];
        return action && action.kind === "interaction";
      });

      // Sync semantics: interaction cohorts are unordered.
      // We encode this as a complete directed relation between all cohort members.
      for (let i = 0; i < interactionIds.length; i += 1) {
        for (let j = 0; j < interactionIds.length; j += 1) {
          if (i === j) continue;
          const fromAction = context.actionsById[interactionIds[i]];
          const toAction = context.actionsById[interactionIds[j]];
          const fromNodeId = activityPostNodeId(fromAction.id);
          const toNodeId = activityPostNodeId(toAction.id);
          addEdge(graph, {
            id: `sync:${fromNodeId}->${toNodeId}@${connectionId}`,
            kind: "sync",
            from: fromNodeId,
            to: toNodeId,
            roleId: fromAction.roleId,
            threadId: fromAction.threadId,
            actionId: fromAction.id,
            connectionId,
            temporalOrder: "forward",
          });
        }
      }
    },
  );

  toSortedIds((rad.threads ?? []).map((thread) => thread.id)).forEach((threadId) => {
    const thread = context.threadsById[threadId];
    if (!thread?.parentActionId) return;
    const parentAction = context.actionsById[thread.parentActionId];
    if (!parentAction) return;

    const childEntryStateId = resolveThreadEntryStateId(thread.id, context);
    const childExitStateId = resolveThreadExitStateId(thread.id, context);
    const roleId = parentAction.roleId ?? context.threadsById[parentAction.threadId]?.roleId;

    // Refinement enter/exit edges "inline" subthreads into forward time.
    if (childEntryStateId && parentAction.pre) {
      addEdge(graph, {
        id: `enter:${parentAction.id}->${thread.id}`,
        kind: "refinement_enter",
        from: stateNodeId(parentAction.pre),
        to: stateNodeId(childEntryStateId),
        roleId,
        threadId: parentAction.threadId,
        actionId: parentAction.id,
        temporalOrder: "forward",
      });
    }

    if (childExitStateId && parentAction.post) {
      addEdge(graph, {
        id: `exit:${thread.id}->${parentAction.id}`,
        kind: "refinement_exit",
        from: stateNodeId(childExitStateId),
        to: stateNodeId(parentAction.post),
        roleId,
        threadId: parentAction.threadId,
        actionId: parentAction.id,
        temporalOrder: "forward",
      });
    }
  });

  Object.keys(graph.byRole).forEach((roleId) => {
    graph.byRole[roleId] = toSortedIds(graph.byRole[roleId]);
  });
  Object.keys(graph.byThread).forEach((threadId) => {
    graph.byThread[threadId] = toSortedIds(graph.byThread[threadId]);
  });
  Object.keys(graph.outgoing).forEach((nodeId) => {
    graph.outgoing[nodeId] = toSortedIds(graph.outgoing[nodeId]);
  });
  Object.keys(graph.incoming).forEach((nodeId) => {
    graph.incoming[nodeId] = toSortedIds(graph.incoming[nodeId]);
  });

  return graph;
};

const DEFAULT_FRONTIER_POLICY = {
  blockPastTimeAcrossSync: true,
  blockCaseSiblingCrossSync: true,
  blockPartCaseCrossSync: true,
  stopAtFirstBlockingInteractionPerRole: true,
};

const getThreadAncestors = (threadId, context) => {
  const ancestors = [];
  let current = context.threadsById[threadId];
  while (current) {
    ancestors.push(current.id);
    if (!current.parentActionId) break;
    const parentAction = context.actionsById[current.parentActionId];
    if (!parentAction) break;
    current = context.threadsById[parentAction.threadId];
  }
  return ancestors;
};

const isCaseSiblingConflict = (leftThreadId, rightThreadId, context) => {
  const left = context.threadsById[leftThreadId];
  const right = context.threadsById[rightThreadId];
  if (!left || !right) return false;
  if (left.class !== "case" || right.class !== "case") return false;
  return (
    Boolean(left.parentActionId) &&
    Boolean(right.parentActionId) &&
    left.parentActionId === right.parentActionId &&
    left.id !== right.id
  );
};

const refinementKindsAlongAncestors = (threadId, context) => {
  const kinds = [];
  const ancestors = getThreadAncestors(threadId, context);
  ancestors.forEach((ancestorThreadId) => {
    const thread = context.threadsById[ancestorThreadId];
    if (!thread?.parentActionId) return;
    const parentAction = context.actionsById[thread.parentActionId];
    if (!parentAction) return;
    if (parentAction.kind === "case" || parentAction.kind === "part") {
      kinds.push(parentAction.kind);
    }
  });
  return new Set(kinds);
};

const isPartCaseScopeConflict = (leftThreadId, rightThreadId, context) => {
  const leftKinds = refinementKindsAlongAncestors(leftThreadId, context);
  const rightKinds = refinementKindsAlongAncestors(rightThreadId, context);
  return (
    (leftKinds.has("part") && rightKinds.has("case")) ||
    (leftKinds.has("case") && rightKinds.has("part"))
  );
};

const resolveStartNodeIds = (graph, context, source) => {
  if (!source?.id || !source?.class) return [];
  if (source.class === "state") {
    return graph.nodes[stateNodeId(source.id)] ? [stateNodeId(source.id)] : [];
  }
  if (source.class === "activity") {
    return graph.nodes[activityPostNodeId(source.id)]
      ? [activityPostNodeId(source.id)]
      : [];
  }
  if (source.class === "connection") {
    const connection = context.connectionsById[source.id];
    if (!connection) return [];
    return toSortedIds(
      (connection.interactionIds ?? []).map((interactionId) =>
        graph.nodes[activityPostNodeId(interactionId)]
          ? activityPostNodeId(interactionId)
          : null,
      ),
    );
  }
  if (source.class === "thread") {
    return toSortedIds(graph.byThread[source.id] ?? []);
  }
  if (source.class === "role") {
    return toSortedIds(graph.byRole[source.id] ?? []);
  }
  return [];
};

export const computeReachabilityFrontier = (
  graph,
  context,
  source,
  policy = {},
) => {
  const resolvedPolicy = { ...DEFAULT_FRONTIER_POLICY, ...policy };
  const startNodeIds = resolveStartNodeIds(graph, context, source);
  // Frontier traversal is breadth-first over graph nodes.
  // `blockedNodeReasons` records the first policy reason that prevented visiting a node.
  const visited = new Set();
  const blockedNodeReasons = {};
  // Per role, once the frontier commits to an interaction cohort, other interactions in that
  // role are blocked (first-interaction guard) to prevent cross-thread time travel.
  const firstInteractionByRole = {};
  const queue = [...startNodeIds];

  while (queue.length > 0) {
    const nodeId = queue.shift();
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);

    const currentNode = graph.nodes[nodeId];
    if (
      resolvedPolicy.stopAtFirstBlockingInteractionPerRole &&
      currentNode?.kind === "activity_post" &&
      currentNode.actionId
    ) {
      const currentAction = context.actionsById[currentNode.actionId];
      if (
        currentAction?.kind === "interaction" &&
        !firstInteractionByRole[currentNode.roleId]
      ) {
        firstInteractionByRole[currentNode.roleId] = new Set([currentNode.actionId]);
      }
    }

    const outgoing = graph.outgoing[nodeId] ?? [];
    outgoing.forEach((edgeId) => {
      const edge = graph.edges[edgeId];
      if (!edge?.to || visited.has(edge.to)) return;
      const toNode = graph.nodes[edge.to];

      if (edge.kind === "sync") {
        const fromNode = graph.nodes[edge.from];
        const fromThreadId = fromNode?.threadId;
        const toThreadId = toNode?.threadId;

        if (
          resolvedPolicy.blockCaseSiblingCrossSync &&
          fromThreadId &&
          toThreadId &&
          isCaseSiblingConflict(fromThreadId, toThreadId, context)
        ) {
          blockedNodeReasons[edge.to] = "blocked_case_scope";
          return;
        }

        if (
          resolvedPolicy.blockPartCaseCrossSync &&
          fromThreadId &&
          toThreadId &&
          isPartCaseScopeConflict(fromThreadId, toThreadId, context)
        ) {
          blockedNodeReasons[edge.to] = "blocked_part_case_scope";
          return;
        }

      }

      if (
        resolvedPolicy.stopAtFirstBlockingInteractionPerRole &&
        toNode?.kind === "activity_post" &&
        toNode.actionId
      ) {
        const targetAction = context.actionsById[toNode.actionId];
        const lockedActionSet = firstInteractionByRole[toNode.roleId];
        if (targetAction?.kind === "interaction") {
          if (!lockedActionSet) {
            let seedSet = new Set([toNode.actionId]);
            if (edge.kind === "sync") {
              // For sync edges, all same-role interactions in the same connection are equivalent.
              // Seed the whole cohort so equivalent members are not blocked as "later".
              const connection = context.connectionsById[edge.connectionId];
              const sameRoleCohort = toSortedIds(
                (connection?.interactionIds ?? []).filter((actionId) => {
                  const action = context.actionsById[actionId];
                  return action?.kind === "interaction" && action.roleId === toNode.roleId;
                }),
              );
              seedSet = new Set(sameRoleCohort.length ? sameRoleCohort : [toNode.actionId]);
            }
            firstInteractionByRole[toNode.roleId] = seedSet;
          } else if (!lockedActionSet.has(toNode.actionId)) {
            blockedNodeReasons[edge.to] = "blocked_by_first_interaction_guard";
            return;
          }
        }
      }

      queue.push(edge.to);
    });
  }

  const stateIdToNodeIds = {};
  const actionIdToNodeIds = {};
  Object.keys(graph.nodes).forEach((nodeId) => {
    const node = graph.nodes[nodeId];
    if (node.stateId) {
      addToMapList(stateIdToNodeIds, node.stateId, nodeId);
    }
    if (node.actionId) {
      addToMapList(actionIdToNodeIds, node.actionId, nodeId);
    }
  });

  const allowedStateIds = [];
  const allowedActionIds = [];
  const allowedRoleIds = [];
  visited.forEach((nodeId) => {
    const node = graph.nodes[nodeId];
    if (!node) return;
    if (node.kind === "state" && node.stateId) {
      allowedStateIds.push(node.stateId);
      allowedRoleIds.push(node.roleId);
    }
    if ((node.kind === "activity_pre" || node.kind === "activity_post") && node.actionId) {
      allowedActionIds.push(node.actionId);
      allowedRoleIds.push(node.roleId);
    }
  });

  // Projection back to domain IDs:
  // - allowed* are collected from visited nodes
  // - blocked* is computed for every domain ID not present in allowed*
  const blocked = {};
  Object.keys(context.statesById).forEach((id) => {
    if (!allowedStateIds.includes(id)) {
      const reasons = (stateIdToNodeIds[id] ?? [])
        .map((nodeId) => blockedNodeReasons[nodeId])
        .filter(Boolean);
      blocked[id] = reasons[0] ?? "not_reachable";
    }
  });
  Object.keys(context.actionsById).forEach((id) => {
    if (!allowedActionIds.includes(id)) {
      const reasons = (actionIdToNodeIds[id] ?? [])
        .map((nodeId) => blockedNodeReasons[nodeId])
        .filter(Boolean);
      blocked[id] = reasons[0] ?? "not_reachable";
    }
  });
  Object.keys(context.threadsById).forEach((id) => {
    if (!(graph.byThread[id] ?? []).some((nodeId) => visited.has(nodeId))) {
      blocked[id] = "not_reachable";
    }
  });
  Object.keys(context.connectionsById).forEach((id) => {
    blocked[id] = "not_reachable";
  });

  return {
    source,
    policy: resolvedPolicy,
    visitedNodeIds: toSortedIds([...visited]),
    acceptedNodeIds: toSortedIds([...visited]),
    allowedStateIds: toSortedIds(allowedStateIds),
    allowedActionIds: toSortedIds(allowedActionIds),
    allowedRoleIds: toSortedIds(allowedRoleIds),
    blocked,
  };
};

export const createReachabilityEngine = () => ({
  buildGraph: buildReachabilityGraph,
  buildContext: buildReachabilityContext,
  computeFrontier: computeReachabilityFrontier,
});
